import { oklchOf, srgbOf } from "../design-system/oklch.ts"

/*
 * The installed app's chrome colours: the status bar, the title bar and the splash screen behind
 * the icon. Each is the canvas, `--sidebar`, in its scheme, so the chrome runs on into the page.
 * The compact layout needs the panel, `--background`, as well: the page runs to the screen's edges
 * there, so the chrome meets the page header rather than the canvas, and the first paint is the
 * panel too.
 *
 * `tokens.css` stays the one place a colour is named. These are worked out from it at build, by
 * Vite's config in Node, and reach the manifest, the `theme-color` metas and the first paint in
 * `index.html` as the sRGB those can carry, and the runtime through `__THEME_COLOURS__`.
 */

export type Rgb = readonly [red: number, green: number, blue: number]

export type Scheme = "light" | "dark"

/** The block each scheme's tokens are declared in: `:root, .light` opens light, one a line. */
const blockOpening: Record<Scheme, string> = { light: "\n.light {", dark: "\n.dark {" }

/** A colour token (`sidebar`) in `tokens.css`'s block for a scheme, as 8-bit sRGB. */
export function colourOf(tokens: string, scheme: Scheme, token: string): Rgb {
  const start = tokens.indexOf(blockOpening[scheme])
  const block = start === -1 ? "" : tokens.slice(start, tokens.indexOf("\n}", start))
  const written = new RegExp(`--${token}:\\s*([^;]+);`).exec(block)?.[1] ?? ""
  const colour = oklchOf(written)
  if (!colour) throw new Error(`tokens.css has no OKLCH --${token} in its ${scheme} block.`)
  return srgbOf(colour)
}

/** `--sidebar`, the canvas, in a scheme's block: the chrome's colour. */
export const sidebarOf = (tokens: string, scheme: Scheme) => colourOf(tokens, scheme, "sidebar")

/** A colour as CSS and a manifest write it: `#` and two hex digits a channel. */
export const cssOf = (rgb: Rgb) =>
  `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`

export type ThemeColours = Record<Scheme, string>

/** Both schemes' chrome colours, as CSS, from the text of `tokens.css`. */
export function themeColoursOf(tokens: string): ThemeColours {
  return { light: cssOf(sidebarOf(tokens, "light")), dark: cssOf(sidebarOf(tokens, "dark")) }
}

/**
 * Both schemes' panel, `--background`, as CSS: the page's own surface, which is the page header's.
 * On the compact layout it is the first paint's colour rather than the canvas, since the page runs
 * to the screen's edges there and Safari tints its chrome from the page.
 */
export function panelColoursOf(tokens: string): ThemeColours {
  return {
    light: cssOf(colourOf(tokens, "light", "background")),
    dark: cssOf(colourOf(tokens, "dark", "background")),
  }
}

/**
 * The first paint's colour, as `index.html`'s inline style: the canvas on the desk and the panel on
 * the compact layout (`compactQuery`), in the device's scheme. It holds only until the colour scheme
 * puts `.light` or `.dark` on `<html>`, after which `tokens.css` decides, so a Member's own choice
 * of scheme isn't overruled by the device's.
 */
export function firstPaintOf(canvas: ThemeColours, panel: ThemeColours, compactQuery: string) {
  const html = "html:not(.light, .dark)"
  const dark = "(prefers-color-scheme: dark)"
  return [
    `${html} { background: ${canvas.light}; }`,
    `@media ${dark} { ${html} { background: ${canvas.dark}; } }`,
    `@media ${compactQuery} { ${html} { background: ${panel.light}; } }`,
    `@media ${compactQuery} and ${dark} { ${html} { background: ${panel.dark}; } }`,
  ].join(" ")
}

/** The chrome's colours in both schemes: the canvas on the desk, the panel on the compact layout. */
export type ChromeColours = { canvas: ThemeColours; panel: ThemeColours }

/** A `theme-color` meta: its colour, and the media it holds under (always, without one). */
export type ThemeColourMeta = { media?: string; content: string }

/**
 * The `theme-color` metas, in the order a browser reads them, since it takes the first whose media
 * matches: the panel on the compact layout (`compactQuery`), where the page header meets the
 * chrome, then the canvas. Without `scheme` they follow the device's scheme (`index.html`'s); with
 * one, they hold whatever the device's is (a Theme the Member chose).
 */
export function themeColourMetasOf(
  { canvas, panel }: ChromeColours,
  compactQuery: string,
  scheme?: Scheme,
): ThemeColourMeta[] {
  if (scheme) return [{ media: compactQuery, content: panel[scheme] }, { content: canvas[scheme] }]
  const schemes = ["light", "dark"] as const
  const device = (each: Scheme) => `(prefers-color-scheme: ${each})`
  return [
    ...schemes.map((each) => ({
      media: `${compactQuery} and ${device(each)}`,
      content: panel[each],
    })),
    ...schemes.map((each) => ({ media: device(each), content: canvas[each] })),
  ]
}
